import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { DashboardCard } from '../components/Dashboard/DashboardCard'
import ArrowUpStemIcon from '@floriday/floriday-icons/ArrowUpStemIcon'
import ArrowDownStemIcon from '@floriday/floriday-icons/ArrowDownStemIcon'
import {
  brandOrange,
  brandPurple,
} from '@floriday/floriday-ui-style-rfh/colors'
import { withErrorBoundary } from '../components/hoc/withErrorBoundary'
import { TransactionSection } from '../components/Transactions/TransactionSection'
import { SnackbarManager } from '@floriday/floriday-ui'
import { useQuery } from '@tanstack/react-query'
import { getCustomerSpendingLimit } from '../services/customerService'
import i18n from '../i18n'
import { useCollectingFormContext } from '../context/collectingContext'
import { useActiveCustomerNumber } from '../hooks/useActiveCustomerNumber'
import { useEffect } from 'react'

export const Dashboard = () => {
  const { t } = useTranslation()
  const {
    values,
    spendingLimitShownKey,
    setSpendingLimitShownKey,
    isBusinessAssociationSelectionReady,
  } = useCollectingFormContext()
  const activeCustomerNumber = useActiveCustomerNumber()
  const spendingLimitContextKey = `${values.userName}|${activeCustomerNumber ?? 'default'}|${i18n.language}`

  const { data: spendingLimit } = useQuery({
    queryKey: ['customerSpendingLimit', activeCustomerNumber, i18n.language],
    queryFn: () =>
      getCustomerSpendingLimit(activeCustomerNumber as number, i18n.language),
    enabled: !!activeCustomerNumber && isBusinessAssociationSelectionReady,
  })

  useEffect(() => {
    if (!isBusinessAssociationSelectionReady) {
      return
    }

    const spendingLimitType = spendingLimit?.type.toLowerCase()

    if (spendingLimitType === 'none') {
      return
    }

    if (!spendingLimit?.message) {
      return
    }

    // Kept on the shared form context (not a local ref) so it survives Dashboard
    // remounts caused by unrelated navigation, e.g. an edit-permission redirect.
    if (spendingLimitContextKey === spendingLimitShownKey) {
      return
    }

    if (spendingLimitType === 'warning') {
      setSpendingLimitShownKey(spendingLimitContextKey)
      SnackbarManager.showWarning(spendingLimit.message)
      return
    }

    if (spendingLimitType === 'error' || spendingLimitType === 'blocked') {
      setSpendingLimitShownKey(spendingLimitContextKey)
      SnackbarManager.showError(spendingLimit.message)
    }
  }, [
    spendingLimit,
    spendingLimitContextKey,
    spendingLimitShownKey,
    setSpendingLimitShownKey,
    isBusinessAssociationSelectionReady,
  ])

  return (
    <Stack
      sx={{
        gap: 2,
      }}
    >
      <Typography variant='h2'>{t('dashboard.title')}</Typography>
      <DashboardCard
        title={t('dashboard.collecting.title')}
        subtitle={t('dashboard.collecting.subtitle')}
        buttonText={t('dashboard.collecting.buttonText')}
        icon={<ArrowUpStemIcon />}
        color={brandOrange}
        to='/collecting'
      />
      <DashboardCard
        title={t('dashboard.returning.title')}
        subtitle={t('dashboard.returning.subtitle')}
        buttonText={t('dashboard.returning.buttonText')}
        icon={<ArrowDownStemIcon />}
        color={brandPurple}
        to='/returning'
      />
      <TransactionSection
        title={t('dashboard.transactions.title')}
        listLimit={5}
        showViewAllButton
      />
      {/* TODO: will be implemented when we have the data */}
      {/* <InventorySection /> */}
    </Stack>
  )
}

export const Route = createFileRoute('/')({
  component: withErrorBoundary(Dashboard),
})
